/**
 * Universal Website → CRM lead capture.
 *
 * Any form can call:
 *   await sendCRMLead({ name, email, phone, message, course, visa_type, ... })
 *
 * Standard fields map to the CRM payload; everything else goes into additional_data.
 * Pass `submission_id` (the same one given to submitLead) and `button_name`; the last
 * clicked call-to-action is attached automatically once initCtaTracking() has run.
 * CRM failures never throw — forms keep working.
 */

export type CrmLeadType =
  | 'contact'
  | 'enquiry'
  | 'callback'
  | 'booking'
  | 'application'
  | 'registration'
  | 'assessment'
  | 'eligibility'
  | 'general'
  | string;

export type CrmLeadPayload = {
  name: string | null;
  email: string | null;
  phone: string | null;
  lead_type: CrmLeadType;
  form_name: string;
  message: string | null;
  additional_data: Record<string, unknown>;
  source_page: string;
  source_url: string;
  submitted_at: string;
  /** Same id as the Admin Portal submit; the CRM treats repeats of it as one lead. */
  external_id: string | null;
  /** Label of the button that submitted the form, e.g. "Check Eligibility". */
  button_name: string | null;
  /** Last call-to-action clicked before the form, e.g. "Book a Consultation". */
  cta_name: string | null;
  cta_page: string | null;
};

export type SendCrmLeadResult = {
  ok: boolean;
  skipped?: boolean;
  status?: number;
  error?: string;
  data?: unknown;
};

const STANDARD_KEYS = new Set([
  'name',
  'first_name',
  'last_name',
  'email',
  'phone',
  'lead_type',
  'form_name',
  'message',
  'additional_data',
  'source_page',
  'source_url',
  'submitted_at',
  'external_id',
  'submission_id',
  'session_id',
  'button_name',
  'cta_name',
  'cta_page',
  // Internal / anti-spam — never send to CRM as lead fields
  'website_url',
  'company_website',
  'turnstileToken',
  '_formLoadedAt',
  'honeypot',
  'delivery_channel',
]);

// --- Call-to-action tracking ---------------------------------------------

const LAST_CTA_KEY = 'hola_last_cta_v1';
const CTA_TTL_MS = 60 * 60 * 1000;

type StoredCta = { label: string; page: string; at: number };

function cleanLabel(raw: string | null | undefined): string | null {
  const s = String(raw || '').replace(/\s+/g, ' ').trim();
  return s ? s.slice(0, 120) : null;
}

function labelOf(el: Element): string | null {
  return (
    cleanLabel(el.getAttribute('data-cta')) ||
    cleanLabel(el.getAttribute('aria-label')) ||
    cleanLabel((el as HTMLElement).innerText || el.textContent) ||
    cleanLabel(el.getAttribute('title'))
  );
}

let ctaTrackingStarted = false;

/**
 * Remember the last button / link the visitor clicked (outside forms), so a lead
 * can say it came from e.g. "Book a Consultation". Call once on app boot.
 */
export function initCtaTracking(): void {
  if (ctaTrackingStarted || typeof document === 'undefined') return;
  ctaTrackingStarted = true;
  document.addEventListener(
    'click',
    (event) => {
      try {
        const target = event.target as Element | null;
        const el = target?.closest?.('button, a, [role="button"], [data-cta]');
        if (!el || el.closest('form')) return;
        const label = labelOf(el);
        if (!label) return;
        const record: StoredCta = { label, page: window.location.pathname, at: Date.now() };
        sessionStorage.setItem(LAST_CTA_KEY, JSON.stringify(record));
      } catch {
        // tracking must never interfere with clicks
      }
    },
    true
  );
}

export function getLastCta(): { label: string; page: string } | null {
  try {
    const raw = sessionStorage.getItem(LAST_CTA_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredCta;
    if (!parsed?.label || Date.now() - parsed.at > CTA_TTL_MS) return null;
    return { label: parsed.label, page: parsed.page };
  } catch {
    return null;
  }
}

/** Label of the button that submitted a form event, falling back to `fallback`. */
export function getSubmitButtonLabel(
  event: { nativeEvent?: Event } | Event | null | undefined,
  fallback = 'Submit'
): string {
  try {
    const native = (event && 'nativeEvent' in event ? event.nativeEvent : event) as SubmitEvent | undefined;
    const submitter = native?.submitter as Element | null | undefined;
    const fromSubmitter = submitter ? labelOf(submitter) : null;
    if (fromSubmitter) return fromSubmitter;
    const form = (native?.target as HTMLFormElement | null) || null;
    const btn = form?.querySelector?.('button[type="submit"], input[type="submit"]');
    const fromForm = btn ? labelOf(btn) || cleanLabel((btn as HTMLInputElement).value) : null;
    return fromForm || fallback;
  } catch {
    return fallback;
  }
}

function env(name: string): string {
  return String(process.env[name] || '').trim();
}

function crmLeadUrl(): string {
  return (
    env('REACT_APP_CRM_LEAD_URL') ||
    env('REACT_APP_VITE_CRM_LEAD_URL') ||
    'https://migration.holaworld.au/api/v1/website/leads'
  );
}

function crmLeadSecret(): string {
  return (
    env('REACT_APP_CRM_LEAD_SECRET') ||
    env('REACT_APP_VITE_CRM_LEAD_SECRET') ||
    env('VITE_CRM_LEAD_SECRET') ||
    ''
  );
}

function asTrimmedString(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
}

function inferLeadType(formName: string, explicit?: unknown): CrmLeadType {
  const given = asTrimmedString(explicit);
  if (given) return given;

  const f = formName.toLowerCase();
  if (f.includes('eligibility') || f.includes('assessment')) return 'assessment';
  if (f.includes('callback')) return 'callback';
  if (f.includes('book')) return 'booking';
  if (f.includes('register') || f.includes('registration')) return 'registration';
  if (f.includes('application') || f.includes('apply')) return 'application';
  if (f.includes('enquiry') || f.includes('inquiry')) return 'enquiry';
  if (f.includes('contact')) return 'contact';
  return 'general';
}

function buildFullName(input: Record<string, unknown>): string | null {
  const name = asTrimmedString(input.name);
  if (name) return name;
  const first = asTrimmedString(input.first_name);
  const last = asTrimmedString(input.last_name);
  if (first && last) return `${first} ${last}`;
  return first || last;
}

/**
 * Convert any form bag into the universal CRM lead structure.
 */
export function normalizeCrmLead(input: Record<string, unknown> = {}): CrmLeadPayload {
  const formName =
    asTrimmedString(input.form_name) ||
    asTrimmedString(input.source_form) ||
    'custom_form';

  const explicitAdditional =
    input.additional_data && typeof input.additional_data === 'object' && !Array.isArray(input.additional_data)
      ? { ...(input.additional_data as Record<string, unknown>) }
      : {};

  const additional_data: Record<string, unknown> = { ...explicitAdditional };

  for (const [key, value] of Object.entries(input)) {
    if (STANDARD_KEYS.has(key) || key === 'source_form' || key === 'form_position') continue;
    if (value === undefined) continue;
    additional_data[key] = value;
  }

  // Keep useful known extras discoverable even if already standard-adjacent
  if (input.country != null && additional_data.country === undefined) {
    additional_data.country = input.country;
  }
  if (input.country_code != null && additional_data.country_code === undefined) {
    additional_data.country_code = input.country_code;
  }
  if (input.form_position != null && additional_data.form_position === undefined) {
    additional_data.form_position = input.form_position;
  }

  const lastCta = getLastCta();

  const sourcePage =
    asTrimmedString(input.source_page) ||
    (typeof window !== 'undefined' ? window.location.pathname : '') ||
    '';

  const sourceUrl =
    asTrimmedString(input.source_url) ||
    (typeof window !== 'undefined' ? window.location.href : '') ||
    '';

  const submittedAt =
    asTrimmedString(input.submitted_at) || new Date().toISOString();

  return {
    name: buildFullName(input),
    email: asTrimmedString(input.email),
    phone: asTrimmedString(input.phone),
    lead_type: inferLeadType(formName, input.lead_type),
    form_name: formName,
    message: asTrimmedString(input.message),
    additional_data,
    source_page: sourcePage,
    source_url: sourceUrl,
    submitted_at: submittedAt,
    external_id: asTrimmedString(input.external_id) || asTrimmedString(input.submission_id),
    button_name: asTrimmedString(input.button_name),
    cta_name: asTrimmedString(input.cta_name) || lastCta?.label || null,
    cta_page: asTrimmedString(input.cta_page) || lastCta?.page || null,
  };
}

let inFlightKey: string | null = null;

function fingerprint(payload: CrmLeadPayload): string {
  if (payload.external_id) return payload.external_id;
  return [
    payload.form_name,
    payload.email || '',
    payload.phone || '',
    payload.name || '',
  ].join('|');
}

/**
 * Send any form data to the Migration CRM Leads API.
 * Never throws. Never blocks UX on CRM failure.
 */
export async function sendCRMLead(
  input: Record<string, unknown> = {}
): Promise<SendCrmLeadResult> {
  const payload = normalizeCrmLead(input);
  const url = crmLeadUrl();
  const secret = crmLeadSecret();

  if (!secret) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[crmLeadService] REACT_APP_CRM_LEAD_SECRET is not set — skipping CRM sync');
    }
    return { ok: false, skipped: true, error: 'crm_secret_not_configured' };
  }

  // Require at least a name or email so empty bags are not posted.
  if (!payload.name && !payload.email) {
    return { ok: false, skipped: true, error: 'missing_name_and_email' };
  }

  const key = fingerprint(payload);
  if (inFlightKey === key) {
    return { ok: false, skipped: true, error: 'duplicate_in_flight' };
  }
  inFlightKey = key;

  try {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), 10000) : null;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Website-Lead-Secret': secret,
      },
      body: JSON.stringify(payload),
      signal: controller?.signal,
    });

    if (timer) clearTimeout(timer);

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error('[crmLeadService] CRM sync failed', res.status, text.slice(0, 300));
      return { ok: false, status: res.status, error: `http_${res.status}` };
    }

    const data = await res.json().catch(() => null);
    return { ok: true, status: res.status, data };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'crm_network_error';
    console.error('[crmLeadService] CRM sync error', message);
    return { ok: false, error: message };
  } finally {
    if (inFlightKey === key) inFlightKey = null;
  }
}

export default sendCRMLead;
